import {
    Sheet,
    SheetContent,
    SheetDescription,
    SheetHeader
} from "@/components/ui/sheet";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { toast } from "@/components/ui/use-toast";
import { SetStateAction, useEffect, useMemo, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { LoadingButton } from "@/components/ui/common/loading-button";
import { Button } from "@/components/ui/button";
import { ChevronDown, SaveIcon, PrinterIcon, CreditCard } from 'lucide-react';
import { ErrorType, FormProps, User } from "@/types";
import { MemberTableDatatypes } from "@/types/member";

// Interface for membership plan data from API response
interface MemberMembershipPlan {
    status: string;
    contract_id: number;
    membership_plan_id: number;
    membership_plan_name: string;
}

// Extended member type with proper membership plans structure
interface ExtendedMember extends Omit<MemberTableDatatypes, 'membership_plans'> {
    membership_plans?: MemberMembershipPlan[];
}
import { FacilityBooking, FacilityAddOn } from "@/types/facility/booking";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { SearchComboBox } from "@/components/ui/globalsearch/search-combo-box";
import { SlotSelector } from "@/components/ui/common/slot-selector";
import { useGetFacilitiesQuery, useGetFacilityByIdQuery, useCreateFacilityBookingMutation, useUpdateFacilityBookingMutation, useValidateBookingQuery, useGetFacilityScheduleByIdQuery, useCriteriaValidationQuery, useUpdateBookingStatusMutation } from "@/services/faciltiesApi";
import { useGetInvoiceByIdQuery, useLazyGetInvoiceByIdQuery } from "@/services/invoiceApi";
import { RootState } from "@/redux/store";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { setInvoice } from "@/redux/invoiceSlice";
import { InvoiceExport } from "../../pos/sales_history/table/receipt-component";
import { Facility } from "@/types/facility";
import { FacilitySchedule, FacilityScheduleAddOn } from "@/types/facility/schedule";
import { useGetMemberInSystemQuery } from "@/services/memberApi";
import { useGetOrgTaxTypeQuery } from "@/services/organizationApi";
import { useGetIncomeCategoryByIdQuery } from "@/services/incomeCategoryApi";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { format } from "date-fns";
import { TaxType } from "@/types/enums";
import {
    Calendar,
    Clock,
    Users,
    MapPin,
    Plus,
    CheckCircle2,
    Info
} from "lucide-react";
import { formatToPKR } from "@/lib/helper";
import { FacilitySlot } from "@/types/facility/booking";
// Extended types to handle API response structure


interface ExtendedFacilityAddOn {
    id: number;
    add_on_facility_id: number;
    name: string;
    description?: string;
    price: number;
    type: string; // FacilityAddOnType from API
}

// For add-ons from facilityById API
interface FacilityApiAddOn {
    id?: number;
    add_on_facility_id?: number;
    name?: string;
    description?: string;
    price?: number;
    type?: string;
}

interface ExtendedFacility extends Omit<Facility, 'add_ons'> {
    slots?: FacilitySlot[];
    add_ons: ExtendedFacilityAddOn[];
}

// Type for facility response from getFacilityById with slots
interface FacilityWithSlots extends Omit<Facility, 'add_ons'> {
    slots?: FacilitySlot[];
    add_ons: FacilityApiAddOn[];
}

const FacilityBookingForm = ({ isOpen, setOpen, action, setAction, data, setData, refetch }: FormProps<FacilityBooking>) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const [searchTerm, setSearchTerm] = useState("");
    const [searchMemberTerm, setSearchMemberTerm] = useState("");
    const [selectedFacility, setSelectedFacility] = useState<ExtendedFacility | null>(null);
    const [selectedMember, setSelectedMember] = useState<ExtendedMember | null>(null);
    const [selectedSlot, setSelectedSlot] = useState<FacilitySlot | null>(null);
    const [selectedAddOns, setSelectedAddOns] = useState<FacilityAddOn[]>([]);
    const [facilitySlots, setFacilitySlots] = useState<FacilitySlot[]>([]);
    const [isSaveDropdown, setIsSaveDropdown] = useState(false)
    const [currentScheduleData, setCurrentScheduleData] = useState<FacilitySchedule|undefined>(undefined);

    const orgId = useSelector(
        (state: RootState) => state.auth.userInfo?.user?.org_id
    ) || 0;

    const [createBooking] = useCreateFacilityBookingMutation();
    const [updateBooking] = useUpdateFacilityBookingMutation();
    
    // Fetch organization tax type
    const { data: orgTaxType } = useGetOrgTaxTypeQuery(orgId) || {
        tax_type: "exclusive",
    };

    // Lazy fetch invoice data when invoiceId is available
    const [fetchInvoiceData, { data: fetchedInvoiceData }] = useLazyGetInvoiceByIdQuery();

    // Track if slot or member has been changed in edit mode
    const [slotChanged, setSlotChanged] = useState(false);
    const [memberChanged, setMemberChanged] = useState(false);

    // Track original values for comparison in edit mode
    const [originalSlotId, setOriginalSlotId] = useState<number | null>(null);
    const [originalMemberId, setOriginalMemberId] = useState<number | null>(null);

    // Validate booking when slot is selected (only for add mode or when slot changed in edit mode)
    const {
        data: validationData,
        isLoading: isValidating,
        error: validationError,
        isError: isValidationError,
        refetch: validateBooking
    } = useValidateBookingQuery({
        org_id: orgId,
        schedule_id: selectedSlot?.facility_schedule_id || 0,
        slot_id: selectedSlot?.id || 0
    }, {
        skip: !selectedSlot || !orgId || (action === "edit" && !slotChanged) || (action === "edit" && selectedSlot?.id === originalSlotId)
    });

    // Validate member criteria when both member and facility are selected (only for add mode or when member changed in edit mode)
    const {
        data: criteriaValidationData,
        isLoading: isCriteriaValidating,
        error: criteriaValidationError,
        isError: isCriteriaValidationError,
        refetch: validateCriteria
    } = useCriteriaValidationQuery({
        org_id: orgId,
        member_id: selectedMember?.id as number,
        facility_id: selectedFacility?.id as number
    }, {
        skip: !selectedMember || !selectedFacility || !orgId || (action === "edit" && !memberChanged) || (action === "edit" && selectedMember?.id === originalMemberId)
    });
    const typedCriteriaValidationError = criteriaValidationError as ErrorType;
    const criteriaValidationMessage = criteriaValidationError ? `${typedCriteriaValidationError.data?.detail || typedCriteriaValidationError.data?.message}` : "Member does not meet facility criteria";

    const typedValidationError = validationError as ErrorType;
    const validationMessage = validationError ? `${typedValidationError.data?.detail || typedValidationError.data?.message}` : "This slot is already booked. Please select a different time slot.";

    // Fetch schedule data when slot is selected to get schedule-specific price and add-ons
    // NOTE: This is now handled by the facilityById API which includes add-ons
    const {
        data: scheduleData,
        isLoading: isLoadingSchedule,
        refetch: refetchSchedule
    } = useGetFacilityScheduleByIdQuery({
        org_id: orgId,
        schedule_id: selectedSlot?.facility_schedule_id || 0,
        query: `load_add_ons=true`
    }, {
        skip: !selectedSlot || !selectedSlot.facility_schedule_id
    });

    // Update currentScheduleData when scheduleData changes
    useEffect(() => {
        setCurrentScheduleData(scheduleData);
    }, [scheduleData]);

    // Fetch income category data when schedule is selected
    const {
        data: incomeCategoryData,
        isLoading: isLoadingIncomeCategory
    } = useGetIncomeCategoryByIdQuery(currentScheduleData?.general_info?.income_category_id || 0, {
        skip: !currentScheduleData?.general_info?.income_category_id
    });

    const {
        data: facilitiesData,
        isLoading,
        refetch: refetchList,
    } = useGetFacilitiesQuery({
        orgId: orgId,
        query: `${action == "add" ? `start_date=${format(new Date(), "yyyy-MM-dd")}&only_available_slots=true&` : ""}status=active&is_add_on=false&load_subcategories=true&only_parent=true&sort_key=id&load_slots=true&load_add_ons=true&sort_order=desc&offset=0` + (searchTerm ? `&search_key=${searchTerm}` : "&limit=1000")
    }, {
        skip: !isOpen || !orgId
    });

    // Get facility by ID with slots when a facility is selected
    const {
        data: facilityByIdData,
        isLoading: isLoadingFacilityById,
        refetch: refetchFacilityById
    } = useGetFacilityByIdQuery({
        org_id: orgId,
        id: selectedFacility?.id || 0,
        query: `${action == "add" ? `start_date=${format(new Date(), "yyyy-MM-dd")}&only_available_slots=true&` : ""}load_slots=true&load_add_ons=true`
    }, {
        skip: !selectedFacility || !selectedFacility.id || !orgId,
        // Force refetch when facility changes to ensure fresh slot data
        refetchOnMountOrArgChange: true
    });

    const { data: users, isLoading: loading } = useGetMemberInSystemQuery({
        query: searchMemberTerm,
        limit: 1000,
        offset: 0,
        sort_order: "desc",
        sort_key: "id",
        type: "member",
        status: "active",
        org_id: orgId,
    }, {
        skip: !isOpen || !orgId
    });

    const facilitiesOptions = useMemo(() => {
        return Array.isArray(facilitiesData?.data) ? facilitiesData?.data.map((item: Facility) => ({
            ...item,
            value: item.id,
            label: item.name,
        })) : [];
    }, [facilitiesData]);

    const userOptions = useMemo(() => {
        return Array.isArray(users) ? users as ExtendedMember[] : [];
    }, [users]);


    // Get add-ons from currentScheduleData when slot is selected, otherwise from facilityByIdData
    const scheduleAddOns = useMemo(() => {
        // Priority 1: Use add-ons from currentScheduleData when slot is selected
        if (currentScheduleData?.add_ons && selectedSlot) {
            return currentScheduleData.add_ons.map((addOn: FacilityScheduleAddOn) => ({
                id: addOn.id || 0,
                add_on_facility_id: addOn.add_on_facility_id || 0,
                name: addOn.name || '',
                description: addOn.description || '',
                price: addOn.price || 0,
                type: addOn.type || 'inclusive'
            })) as ExtendedFacilityAddOn[];
        }

        // Priority 2: Fall back to facilityByIdData when no slot is selected
        if (facilityByIdData) {
            const facilityData = facilityByIdData as FacilityWithSlots;
            return (facilityData?.add_ons || []).map(addOn => ({
                id: addOn.id || 0,
                add_on_facility_id: addOn.add_on_facility_id || 0,
                name: addOn.name || '',
                description: addOn.description,
                price: addOn.price || 0,
                type: addOn.type || 'inclusive'
            })) as ExtendedFacilityAddOn[];
        }

        return [];
    }, [currentScheduleData?.add_ons, selectedSlot, facilityByIdData]);

    // Get inclusive add-ons that are always required
    const inclusiveAddOns = useMemo(() => {
        if (!scheduleAddOns) return [];

        // In edit mode, only include inclusive add-ons that were actually selected in the original booking
        if (action === "edit" && data?.add_ons) {
            return scheduleAddOns
                .filter(addOn => addOn.type?.toLowerCase().includes('inclusive'))
                .filter(addOn => data.add_ons?.some(selectedAddOn => selectedAddOn.add_on_facility_id === addOn.add_on_facility_id))
                .map(addOn => ({
                    id: addOn.id,
                    add_on_facility_id: addOn.add_on_facility_id,
                    price: addOn.price || 0,
                    type: TaxType.inclusive
                }));
        }

        // In add mode, include all inclusive add-ons
        return scheduleAddOns
            .filter(addOn => addOn.type?.toLowerCase().includes('inclusive'))
            .map(addOn => ({
                id: addOn.id,
                add_on_facility_id: addOn.add_on_facility_id,
                price: addOn.price || 0,
                type: TaxType.inclusive
            }));
    }, [scheduleAddOns, action, data?.add_ons]); // Use more complete dependency

    // Helper function to calculate special pricing for a given slot date
    const calculateSpecialPrice = useMemo(() => {
        if (!selectedSlot || !selectedSlot.special_pricing || selectedSlot.special_pricing.length === 0) {
            return null;
        }

        // Compare using date-only strings to avoid timezone shifts
        const slotDateStr = format(new Date(selectedSlot.date), 'yyyy-MM-dd');

        const matchingSpecialPrice = selectedSlot.special_pricing.find((specialPrice) => {
            return (
                slotDateStr >= specialPrice.date_from &&
                slotDateStr <= specialPrice.date_to
            );
        });

        return matchingSpecialPrice || null;
    }, [selectedSlot]);

    // Helper function to calculate plan-based pricing
    const calculatePlanPrice = useMemo(() => {
        if (!selectedMember || !selectedSlot || !selectedSlot.plan_pricing || selectedSlot.plan_pricing.length === 0) {
            return null;
        }

        // Get member's active membership plans
        const memberPlans = selectedMember.membership_plans?.filter(plan => plan.status === 'active') || [];

        if (memberPlans.length === 0) {
            return null;
        }

        // Find the first matching plan pricing
        for (const memberPlan of memberPlans) {
            const matchingPlanPrice = selectedSlot.plan_pricing.find(planPrice =>
                Number(planPrice.membership_plan_id) === Number(memberPlan.membership_plan_id)
            );

            if (matchingPlanPrice) {
                return {
                    ...matchingPlanPrice,
                    plan_name: memberPlan.membership_plan_name
                };
            }
        }

        return null;
    }, [selectedMember, selectedSlot]);

    // Calculate the base price as the lowest of all available options
    const basePrice = useMemo(() => {
        const prices: number[] = [];

        // Add facility base price
        if (selectedFacility?.price) {
            prices.push(selectedFacility.price);
        }

        // Add schedule base price
        if (currentScheduleData?.general_info?.price) {
            prices.push(currentScheduleData.general_info.price);
        }

        // Add all special pricing prices that apply to the selected slot
        if (selectedSlot?.special_pricing && selectedSlot.special_pricing.length > 0) {
            const slotDateStr = format(new Date(selectedSlot.date), 'yyyy-MM-dd');
            selectedSlot.special_pricing.forEach((specialPrice) => {
                if (slotDateStr >= specialPrice.date_from && slotDateStr <= specialPrice.date_to) {
                    prices.push(specialPrice.price);
                }
            });
        }

        // Add all plan pricing prices from the schedule
        if (currentScheduleData?.plan_pricing && currentScheduleData.plan_pricing.length > 0) {
            currentScheduleData.plan_pricing.forEach((planPrice) => {
                prices.push(planPrice.price);
            });
        }

        // Add all plan pricing prices from the slot
        if (selectedSlot?.plan_pricing && selectedSlot.plan_pricing.length > 0) {
            selectedSlot.plan_pricing.forEach((planPrice) => {
                prices.push(planPrice.price);
            });
        }

        // Return the minimum price, or 0 if no prices found
        return prices.length > 0 ? Math.min(...prices) : 0;
    }, [selectedFacility?.price, currentScheduleData?.general_info?.price, currentScheduleData?.plan_pricing, selectedSlot?.special_pricing, selectedSlot?.plan_pricing, selectedSlot?.date]);

    // Calculate the effective price with priority: Special Price → Plan Price → Schedule Base Price → Facility Base Price
    const effectivePrice = useMemo(() => {
        // If no slot selected, show facility base price
        if (!selectedSlot) {
            return selectedFacility?.price || 0;
        }

        // Priority 1: Special Price (if slot date falls within special pricing range)
        if (calculateSpecialPrice) {
            return calculateSpecialPrice.price;
        }

        // Priority 2: Plan-based Price (if member has matching membership plan)
        if (calculatePlanPrice) {
            return calculatePlanPrice.price;
        }

        // Priority 3: Schedule Base Price, fallback to facility price
        return currentScheduleData?.general_info?.price || selectedFacility?.price || 0;
    }, [selectedSlot, calculateSpecialPrice, calculatePlanPrice, currentScheduleData?.general_info?.price, selectedFacility?.price]);


    // Calculate subtotal (facility + add-ons) - this is the base amount
    const subtotal = useMemo(() => {
        const selectedAddOnsTotal = selectedAddOns.reduce((total, addOn) => total + addOn.price, 0);

        // In edit mode, ensure all inclusive add-ons are included in the total
        // (they should already be in selectedAddOns, but this is a safety check)
        const inclusiveAddOnsTotal = inclusiveAddOns.reduce((total, addOn) => total + addOn.price, 0);

        // For edit mode, use the inclusive add-ons total to ensure we don't double-count
        // For add mode, use selectedAddOnsTotal which already includes inclusive add-ons
        const addOnsTotal = action === "edit" ?
            Math.max(selectedAddOnsTotal, inclusiveAddOnsTotal) :
            selectedAddOnsTotal;

        return effectivePrice + addOnsTotal;
    }, [effectivePrice, selectedAddOns.length, selectedAddOns.reduce((sum, a) => sum + a.price, 0), inclusiveAddOns.length, action]);

    // Calculate tax amount based on organization tax type (on subtotal)
    const taxCalculation = useMemo(() => {
        if (!incomeCategoryData || !incomeCategoryData.sale_tax_perc || !subtotal) {
            return {
                taxAmount: 0,
                taxPercentage: 0,
                taxName: '',
                subtotal: subtotal,
                total: subtotal
            };
        }

        const taxRate = incomeCategoryData.sale_tax_perc;
        const taxName = incomeCategoryData.sale_tax_name || '';
        let taxAmount = 0;

        if (orgTaxType?.tax_type === "exclusive") {
            // For exclusive tax: calculate tax on subtotal
            taxAmount = subtotal * (taxRate / 100);
        } else {
            // For inclusive tax: calculate the actual tax amount from the inclusive subtotal
            // Formula: taxAmount = subtotal * (taxRate / (100 + taxRate))
            taxAmount = subtotal * (taxRate / (100 + taxRate));
        }

        return {
            taxAmount: Math.round(taxAmount * 100) / 100,
            taxPercentage: taxRate,
            taxName,
            subtotal: Math.round(subtotal * 100) / 100,
            total: Math.round((subtotal + (orgTaxType?.tax_type === "exclusive" ? taxAmount : 0)) * 100) / 100
        };
    }, [subtotal, incomeCategoryData, orgTaxType?.tax_type]);

    // Calculate total price
    const totalPrice = useMemo(() => {
        if (orgTaxType?.tax_type === "inclusive") {
            // For inclusive tax: tax is already included in subtotal
            return subtotal;
        } else {
            // For exclusive tax: add tax to subtotal
            return taxCalculation.subtotal + taxCalculation.taxAmount;
        }
    }, [subtotal, taxCalculation.subtotal, taxCalculation.taxAmount, orgTaxType?.tax_type]);



    // Add default values for all form fields
    const initialValues: Partial<FacilityBooking> = useMemo(() => ({
        org_id: orgId,
        coach_id: undefined,
        member_id: 0,
        facility_id: 0,
        facility_schedule_id: 0,
        slot_id: 0,
        facility_price: 0,
        total: 0,
        add_ons: [],
        is_admin: true
    }), [orgId]);

    const form = useForm<FacilityBooking>({
        mode: "all",
        defaultValues: initialValues,
    });

    const {
        control,
        watch,
        register,
        handleSubmit,
        clearErrors,
        getValues,
        reset,
        setValue,
        formState: { isSubmitting, errors },
    } = form;
    const watcher = watch();

    // Track if we've already done the initial edit mode setup
    const [editModeInitialized, setEditModeInitialized] = useState(false);

    // Track user interactions to prevent automatic resets
    const [userModifiedFacility, setUserModifiedFacility] = useState(false);
    const [userModifiedMember, setUserModifiedMember] = useState(false);

    // Comprehensive initialization effect for edit mode
    useEffect(() => {
        if (action === "edit" && data && isOpen) {
            if (!editModeInitialized) {
                reset(data as any);

                // Set original values for comparison
                setOriginalSlotId(data.slot_id || null);
                setOriginalMemberId(data.member_id || null);
                setSlotChanged(false);
                setMemberChanged(false);
                setUserModifiedFacility(false);
                setUserModifiedMember(false);

                // Set add-ons if present
                if (data.add_ons && data.add_ons.length > 0) {
                    const mappedAddOns: FacilityAddOn[] = data.add_ons.map((addOn: any) => ({
                        id: addOn.id,
                        add_on_facility_id: addOn.add_on_facility_id || addOn.facility_id || 0,
                        price: addOn.price || 0,
                        type: addOn.type?.toLowerCase().includes('inclusive') ? TaxType.inclusive : TaxType.exclusive
                    }));
                    setSelectedAddOns(mappedAddOns);

                    // Also update the form values to include the selected add-ons
                    setValue("add_ons", mappedAddOns);
                }

                // Mark as initialized
                setEditModeInitialized(true);
            }

            // Set facility if available and not modified by user
            if (facilitiesData?.data && data.facility_id && !selectedFacility && !userModifiedFacility) {
                const facility = facilitiesData.data.find(f => f.id === data.facility_id);
                if (facility) {
                    setSelectedFacility(facility as ExtendedFacility);
                    setValue("coach_id", Number(facility.coach_id));
                    setSearchTerm(facility.name);
                }
            }

            // Set member if available and not modified by user
            if (users && data.member_id && !selectedMember && !userModifiedMember) {
                const member = (users as ExtendedMember[]).find(u => u.id === data.member_id);
                if (member) {
                    setSelectedMember(member as ExtendedMember);
                    setSearchMemberTerm(`${member.first_name} ${member.last_name}`);
                }
            }

            // Set slot when facility slots are available
            if (data.slot_id && !selectedSlot && facilitySlots.length > 0) {
                const slot = facilitySlots.find((s: FacilitySlot) => s.id === data.slot_id);
                if (slot) {
                    // Validate slot date before setting
                    try {
                        const testDate = new Date(slot.date)
                        if (!isNaN(testDate.getTime())) {
                            setSelectedSlot(slot);
                        } else {
                            console.warn('Edit mode slot has invalid date:', slot)
                        }
                    } catch (error) {
                        console.warn('Error validating edit mode slot:', slot, error)
                    }
                }
            }
        } else if (action !== "edit") {
            // Reset to initial values when not in edit mode
            reset(initialValues);
            setOriginalSlotId(null);
            setOriginalMemberId(null);
            setSlotChanged(false);
            setMemberChanged(false);
            setEditModeInitialized(false);
            setUserModifiedFacility(false);
            setUserModifiedMember(false);
        }
    }, [action, data, isOpen, editModeInitialized, facilitiesData?.data, users, facilitySlots.length, userModifiedFacility, userModifiedMember]);

    // Update facility slots when facilityByIdData is loaded
    useEffect(() => {
        const facilityData = facilityByIdData as FacilityWithSlots;
        if (facilityData?.slots) {
            // Filter out slots with invalid dates and log warnings
            const validSlots = facilityData.slots.filter((slot: FacilitySlot) => {
                if (!slot.date) {
                    console.warn('Slot missing date:', slot)
                    return false
                }
                try {
                    // slot.date already contains timezone info like "2025-10-26T00:00:00"
                    const testDate = new Date(slot.date)
                    if (isNaN(testDate.getTime())) {
                        console.warn('Slot with invalid date:', slot)
                        return false
                    }
                    return true
                } catch (error) {
                    console.warn('Error validating slot date:', slot, error)
                    return false
                }
            })

            setFacilitySlots(validSlots);

            // In edit mode, immediately set the selected slot if we have the data and it's not already set
            if (action === "edit" && data && data.slot_id && !selectedSlot) {
                const slot = validSlots.find((s: FacilitySlot) => s.id === data.slot_id);
                if (slot) {
                    // Validate slot date before setting
                    try {
                        const testDate = new Date(slot.date)
                        if (!isNaN(testDate.getTime())) {
                            setSelectedSlot(slot);
                        } else {
                            console.warn('Edit mode slot has invalid date:', slot)
                        }
                    } catch (error) {
                        console.warn('Error validating edit mode slot:', slot, error)
                    }
                }
            }
        } else {
            setFacilitySlots([]);
        }
    }, [facilityByIdData, action, data, selectedSlot]);

    // Initialize add-ons when facility or schedule is selected
    useEffect(() => {
        // In edit mode, don't auto-select add-ons if we already have them from the booking data
        if (action === "edit" && selectedAddOns.length > 0) {
            return;
        }

        if (scheduleAddOns && scheduleAddOns.length > 0) {
            // Always include inclusive add-ons (required), and select all exclusive add-ons by default
            const allAddOns: FacilityAddOn[] = scheduleAddOns.map(addOn => ({
                id: addOn.id,
                add_on_facility_id: addOn.add_on_facility_id,
                price: addOn.price || 0,
                type: addOn.type?.toLowerCase().includes('inclusive') ? TaxType.inclusive : TaxType.exclusive
            }));
            setSelectedAddOns(allAddOns);
            // Add-ons come from currentScheduleData when slot is selected, otherwise from facilityByIdData
        } else {
            setSelectedAddOns([]);
        }
    }, [selectedFacility, scheduleAddOns, currentScheduleData, action, selectedSlot]);

    // Update total when prices change
    useEffect(() => {
        setValue("total", totalPrice);
        setValue("facility_price", effectivePrice);
        setValue("add_ons", selectedAddOns);
    }, [totalPrice, effectivePrice, selectedAddOns, setValue]);

    // Add a ref to prevent setValue loops
    const lastSetValues = useRef({ total: 0, facility_price: 0, add_ons: [] as any[] });

    useEffect(() => {
        // Only update if values actually changed
        if (lastSetValues.current.total !== totalPrice) {
            setValue("total", totalPrice);
            lastSetValues.current.total = totalPrice;
        }
        if (lastSetValues.current.facility_price !== effectivePrice) {
            setValue("facility_price", effectivePrice);
            lastSetValues.current.facility_price = effectivePrice;
        }
        // For add_ons, compare by length and content
        const addOnsChanged = lastSetValues.current.add_ons.length !== selectedAddOns.length ||
            lastSetValues.current.add_ons.some((addon, idx) => addon.add_on_facility_id !== selectedAddOns[idx]?.add_on_facility_id);
        if (addOnsChanged) {
            setValue("add_ons", selectedAddOns);
            lastSetValues.current.add_ons = selectedAddOns;
        }
    }, [totalPrice, effectivePrice, selectedAddOns, setValue]);

    // Update onSubmit function to handle the entire form data
    const onSubmit = async (formData: FacilityBooking): Promise<any> => {
        console.log("onSubmit called with formData:", formData);
        try {
            // For add mode, always require validations
            // For edit mode, only require validations if slot or member was changed and not back to original
            if (action === "add") {
                if (!selectedFacility) {
                    toast({
                        variant: "destructive",
                        description: "Please select a facility.",
                    });
                    return;
                }
                if (!selectedMember) {
                    toast({
                        variant: "destructive",
                        description: "Please select a member",
                    });
                    return;
                }
                if (!selectedSlot) {
                    toast({
                        variant: "destructive",
                        description: "Please select a slot.",
                    });
                    return;
                }
                // Check if validation passed
                if (!validationData) {
                    toast({
                        variant: "destructive",
                        description: "Please wait for booking validation to complete.",
                    });
                    return;
                }

                // Check if criteria validation passed
                if (!criteriaValidationData) {
                    toast({
                        variant: "destructive",
                        description: "Please wait for member criteria validation to complete.",
                    });
                    return;
                }
            } else if (action === "edit" && (slotChanged || memberChanged) && !(isBackToOriginalSlot && isBackToOriginalMember)) {
                // Only check validations in edit mode if something was changed and not back to original values
                if (slotChanged && !isBackToOriginalSlot && !validationData) {
                    toast({
                        variant: "destructive",
                        description: "Please wait for booking validation to complete.",
                    });
                    return;
                }

                if (memberChanged && !isBackToOriginalMember && !criteriaValidationData) {
                    toast({
                        variant: "destructive",
                        description: "Please wait for member criteria validation to complete.",
                    });
                    return;
                }
            }

            // Ensure inclusive add-ons are always included in the final payload
            const allRequiredAddOns = action === "edit" ?
                selectedAddOns : // In edit mode, use the selected add-ons as they already include the correct ones
                [...inclusiveAddOns, ...selectedAddOns.filter(addOn =>
                    !inclusiveAddOns.some(inclusive => inclusive.add_on_facility_id === addOn.add_on_facility_id)
                )];

            const payload: FacilityBooking = {
                org_id: orgId,
                member_id: selectedMember?.id || 0,
                facility_id: selectedFacility?.id || 0,
                facility_schedule_id: selectedSlot?.facility_schedule_id || 0,
                slot_id: selectedSlot?.id || 0,
                coach_id: selectedFacility?.coach_id,
                max_attendees: currentScheduleData?.general_info?.max_attendees || 0,
                facility_price: effectivePrice,
                total: totalPrice,
                add_ons: allRequiredAddOns,
                is_admin: true
            };

            let response: any;
            if (action === "edit" && data?.id) {
                payload.id = data.id;
                response = await updateBooking(payload).unwrap();
                toast({
                    variant: "success",
                    description: "Booking updated successfully!",
                });
            } else {
                response = await createBooking(payload).unwrap();
                toast({
                    variant: "success",
                    description: "Booking created successfully!",
                });
            }

            refetch?.();

            return response;
        } catch (error: any) {
            toast({
                variant: "destructive",
                description: error?.data?.message || "Something went wrong. Please try again.",
            });
            return false;
        }
    };

    const onError = () => {
        toast({
            variant: "destructive",
            description: "Please fill all the mandatory fields",
        });
    };

    const handleSaveAndPrint = async () => {
        const resp = await onSubmit(watcher as FacilityBooking);
        if (resp && resp.invoice_id) {
            try {
                // Fetch invoice data using the lazy query
                const result = await fetchInvoiceData({ id: resp.invoice_id });
                if (result.data) {
                    handlePrintInvoice(result.data);
                }
            } catch (error) {
                console.error("Error fetching invoice data:", error);
                toast({
                    variant: "destructive",
                    description: "Error fetching invoice data for printing",
                });
            }
        }
    };

    const handlePrintInvoice = async (invoiceDetails: any) => {
        try {
            // Assuming `InvoiceExport` generates HTML content for the invoice
            const htmlContent = await InvoiceExport(invoiceDetails);

            // Create an invisible iframe
            const iframe = document.createElement("iframe");
            iframe.style.position = "absolute";
            iframe.style.width = "0px";
            iframe.style.height = "0px";
            iframe.style.border = "none";
            document.body.appendChild(iframe);

            const iframeDoc =
                iframe.contentWindow?.document || iframe.contentDocument;
            if (iframeDoc) {
                iframeDoc.open();
                iframeDoc.write(htmlContent);
                iframeDoc.close();

                iframe.onload = () => {
                    iframe.contentWindow?.focus();
                    iframe.contentWindow?.print();

                    setTimeout(() => {
                        document.body.removeChild(iframe); // Cleanup
                        handleClose();
                    }, 100);
                };
            }
        } catch (error) {
            console.error("Error printing invoice:", error);
            toast({
                variant: "destructive",
                description: "Error printing invoice",
            });
        }
    };

    const handleSaveOnly = async () => {
        const resp = await onSubmit(watcher as FacilityBooking);
        if (resp) {
            handleClose();
        }
    };

    const handleSaveAndPay = async () => {
        console.log("handleSaveAndPay called");
        const resp = await onSubmit(watcher as FacilityBooking);
        console.log("onSubmit response:", resp);

        if (resp) {
            console.log("Booking created/updated successfully:", resp);

            // If we have an invoice_id, fetch the invoice data and navigate to payment
            if (resp.invoice_id) {
                console.log("Found invoice_id:", resp.invoice_id);
                try {
                    // Fetch invoice data using the lazy query
                    const result = await fetchInvoiceData({ id: resp.invoice_id });
                    console.log("Invoice fetch result:", result);

                    if (result.data) {
                        console.log("Setting invoice data in Redux:", result.data);
                        dispatch(setInvoice(result.data));

                        // Navigate to counter selection with proper timing
                        console.log("Navigating to counter selection");
                        setTimeout(() => {
                            navigate('/counter-selection');
                            // Close modal after navigation
                            setTimeout(() => {
                                setOpen(false);
                            }, 200);
                        }, 500);
                    } else {
                        console.error("No invoice data in result");
                        toast({
                            variant: "destructive",
                            description: "Failed to fetch invoice data",
                        });
                    }
                } catch (error) {
                    console.error("Error fetching invoice data:", error);
                    toast({
                        variant: "destructive",
                        description: "Error fetching invoice data for payment",
                    });
                }
            } else {
                console.log("No invoice_id in response - booking created but no invoice to pay");
                toast({
                    variant: "destructive",
                    description: "Booking created successfully, but no invoice was generated for payment",
                });
            }
        } else {
            console.log("No response from booking creation/update");
            toast({
                variant: "destructive",
                description: "Booking creation failed",
            });
        }
    };

    const handleClose = () => {
        clearErrors();
        setAction("add");
        setData(undefined);
        setSelectedFacility(null);
        setSelectedMember(null);
        setSelectedSlot(null);
        setSelectedAddOns([]);
        setFacilitySlots([]); // Clear facility slots
        setSearchTerm("");  // Reset facility search
        setSearchMemberTerm("");  // Reset member search
        setSlotChanged(false);  // Reset change flags
        setMemberChanged(false);
        setOriginalSlotId(null);  // Reset original values
        setOriginalMemberId(null);
        setEditModeInitialized(false);  // Reset edit mode initialization flag
        setUserModifiedFacility(false);  // Reset user modification flags
        setUserModifiedMember(false);
        setIsSaveDropdown(false);  // Reset dropdown state
        reset(
            initialValues,
            {
                keepIsSubmitted: false,
                keepSubmitCount: false,
                keepDirtyValues: false,
                keepDefaultValues: true,
            }
        );
        setOpen(false);
    };

    const handleSearchChange = (term: string) => {
        setSearchTerm(term);

        // Mark as user-modified when they start typing or clear the field
        if (action === "edit") {
            setUserModifiedFacility(true);
        }

        // If search term is cleared, also clear the selected facility
        if (!term.trim()) {
            setSelectedFacility(null);
            setFacilitySlots([]); // Clear facility slots
            setValue("facility_id", 0);
            setValue("coach_id", undefined);
            setValue("facility_price", 0);
            setValue("slot_id", 0);
            setValue("facility_schedule_id", 0);
            setValue("add_ons", []);
            setValue("total", 0);
            setSelectedSlot(null);
            setSelectedAddOns([]);
            // Reset change flags when clearing
            if (action === "edit") {
                setSlotChanged(false);
            }
        }
    };

    const handleSearchMemberChange = (term: string) => {
        setSearchMemberTerm(term);

        // Mark as user-modified when they start typing or clear the field
        if (action === "edit") {
            setUserModifiedMember(true);
        }

        // If search term is cleared, also clear the selected member
        if (!term.trim()) {
            setSelectedMember(null);
            setValue("member_id", 0);
            // Reset change flags when clearing
            if (action === "edit") {
                setMemberChanged(false);
            }
        }
    };

    const handleFacilitySelect = (selectedFacility: Facility | null) => {
        console.log("Selected Facility:", selectedFacility);

        if (!selectedFacility) return;

        setSelectedFacility(selectedFacility as ExtendedFacility);
        setSelectedSlot(null); // Reset slot when facility changes
        setSelectedAddOns([]); // Reset add-ons when facility changes
        setFacilitySlots([]); // Clear current slots - will be loaded by getFacilityById
        setCurrentScheduleData(undefined); // Clear schedule data from previous facility
        setSearchTerm(selectedFacility.name); // Update search term to show new facility name
        setValue("facility_id", Number(selectedFacility.id));
        setValue("facility_price", Number(selectedFacility.price));
        setValue("coach_id", Number(selectedFacility.coach_id));
        setValue("slot_id", 0); // Reset slot_id in form
        setValue("facility_schedule_id", 0); // Reset schedule_id in form
        setValue("add_ons", []); // Reset add-ons in form
        setValue("total", Number(selectedFacility.price)); // Reset total to base price
        // Note: scheduleData will be cleared when selectedSlot becomes null due to the skip condition
    };

    const handleMemberSelect = (selectedMember: ExtendedMember | null) => {
        console.log("Selected Member:", selectedMember);

        if (!selectedMember) return;

        setSelectedMember(selectedMember);
        setValue("member_id", Number(selectedMember.id));

        // Mark member as changed if in edit mode
        if (action === "edit") {
            setMemberChanged(true);
        }
    };

    const handleSlotSelect = (slot: FacilitySlot) => {
        setSelectedSlot(slot);
        setValue("slot_id", slot.id);
        setValue("facility_schedule_id", slot.facility_schedule_id);

        // Mark slot as changed if in edit mode
        if (action === "edit") {
            setSlotChanged(true);
        }
    };

    const handleAddOnToggle = (addOn: ExtendedFacilityAddOn, checked: boolean) => {
        // Prevent removal of inclusive add-ons
        const isInclusive = addOn.type?.toLowerCase().includes('inclusive');
        if (isInclusive && !checked) {
            // Don't allow unchecking of inclusive add-ons
            return;
        }

        if (checked) {
            const newAddOn: FacilityAddOn = {
                id: addOn.id,
                price: addOn.price || 0,
                add_on_facility_id: addOn.add_on_facility_id,
                type: addOn.type?.toLowerCase().includes('inclusive') ? TaxType.inclusive : TaxType.exclusive
            };
            // Check if already exists to avoid duplicates
            const exists = selectedAddOns.some(item => item.add_on_facility_id === addOn.add_on_facility_id);
            if (!exists) {
                setSelectedAddOns(prev => [...prev, newAddOn]);
            }
        } else {
            // Only allow removal of exclusive add-ons
            setSelectedAddOns(prev => prev.filter(item => item.add_on_facility_id !== addOn.add_on_facility_id));
        }
    };

    // Form validation logic
    const hasRequiredSelections = selectedFacility && selectedMember && selectedSlot;

    // Check if any validation is currently running
    const isAnyValidationRunning = isValidating || isCriteriaValidating || isLoadingFacilityById;

    // Check if there are any validation errors
    const hasValidationErrors = validationError || criteriaValidationError;

    // Check if all validations have passed
    const allValidationsPassed = validationData && criteriaValidationData && !hasValidationErrors;

    // For edit mode, check if we're back to original values
    const isBackToOriginalSlot = action === "edit" && selectedSlot?.id === originalSlotId;
    const isBackToOriginalMember = action === "edit" && selectedMember?.id === originalMemberId;

    // Form is valid when:
    // 1. All required selections are made
    // 2. No validation is currently running
    // 3. For add mode: all validations have passed
    // 4. For edit mode: either no validations needed (nothing changed or back to original) or all validations have passed
    const isFormValid = isValidationError || isCriteriaValidationError

    console.log({ currentScheduleData, watcher, action, selectedFacility, selectedSlot, selectedMember });


    return (
        <Sheet open={isOpen} onOpenChange={handleClose}>
            <SheetContent
                hideCloseButton
                className="bg-formbg w-full lg:max-w-4xl p-0 custom-scrollbar h-full"
            >
                <FormProvider {...form}>
                    <form
                        key={action}
                        noValidate
                        className=" flex flex-col"
                        onSubmit={handleSubmit(onSubmit, onError)}
                    >
                        <SheetHeader className="px-6 sticky top-0 z-40 py-4 bg-white shadow-sm border-b">
                            <div className="flex items-center justify-between gap-2 w-full">
                                <div className="flex items-start gap-3">
                                    <button type="button" onClick={handleClose}>
                                        <i className="fa fa-xmark text-xl text-gray-600 hover:text-gray-800"></i>
                                    </button>
                                    <div>
                                        <h1 className="text-xl font-semibold text-gray-900">
                                            {action == "add" ? "Create" : "Edit"} Booking
                                        </h1>
                                        <p className="text-sm text-gray-600">
                                            Book a facility for a member
                                        </p>
                                    </div>
                                </div>

                                <div className="flex items-center gap-2">
                                    <DropdownMenu open={isSaveDropdown} onOpenChange={setIsSaveDropdown}>
                                        <DropdownMenuTrigger asChild>
                                            <Button type="button" className="cursor-pointer bg-primary hover:bg-primary flex items-center gap-2">
                                                <i className="fa fa-floppy-disk"></i>
                                                {action == "add" ? "Save" : "Update"}
                                                <ChevronDown className="h-4 w-4 opacity-70" />
                                            </Button>
                                        </DropdownMenuTrigger>
                                        <DropdownMenuContent align="end" className="w-auto">
                                            <DropdownMenuItem asChild onClick={handleSaveOnly}>
                                                <Button
                                                    className="cursor-pointer w-full hover:bg-slate-100 bg-white text-black flex justify-start gap-2 h-8"
                                                >
                                                    <SaveIcon className="size-4" />
                                                    <span className=" ">
                                                        {action === "edit" ? "Update" : "Save"}
                                                    </span>
                                                </Button>
                                            </DropdownMenuItem>
                                            <DropdownMenuItem asChild onClick={handleSaveAndPrint}>
                                                <Button
                                                    className="cursor-pointer w-full hover:bg-slate-100 bg-white text-black text-nowrap flex justify-start gap-2 h-8 my-2"
                                                >
                                                    <PrinterIcon className="size-4" />
                                                    <span className="">
                                                        {action === "edit" ? "Update & Print" : "Save & Print"}
                                                    </span>
                                                </Button>
                                            </DropdownMenuItem>
                                            <DropdownMenuItem asChild onClick={handleSaveAndPay}>
                                                <Button
                                                    className="cursor-pointer w-full hover:bg-slate-100 bg-white text-black text-nowrap justify-start flex gap-2 h-8"
                                                >
                                                    <CreditCard className="size-4" />
                                                    <span className="">
                                                        {action === "edit" ? "Update & Pay" : "Save & Pay"}
                                                    </span>
                                                </Button>
                                            </DropdownMenuItem>
                                        </DropdownMenuContent>
                                    </DropdownMenu>
                                </div>
                            </div>
                        </SheetHeader>

                        <div className="px-6 py-6 space-y-6">
                            {/* Basic Information */}
                            <Card className="p-6 shadow-sm border-l-4 border-l-blue-500">
                                <div className="flex items-center gap-3 mb-4">
                                    <div className="p-2 bg-blue-100 rounded-lg">
                                        <Users className="h-5 w-5 text-blue-600" />
                                    </div>
                                    <div>
                                        <h2 className="text-lg font-semibold text-gray-900">Basic Information</h2>
                                        <p className="text-sm text-gray-600">Select facility and member for this booking</p>
                                    </div>
                                </div>
                                <Separator className="mb-6" />

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div>
                                        <Label className="text-sm font-medium text-gray-700 mb-2 block">
                                            Facility <span className="text-red-500">*</span>
                                        </Label>
                                        <SearchComboBox
                                            items={facilitiesOptions || []}
                                            onSearchChange={handleSearchChange}
                                            onItemSelect={handleFacilitySelect}
                                            value={searchTerm}
                                            placeholder="Start typing to search facilities..."
                                            loading={isLoading}
                                            getItemKey={(facility) => facility.id || ""}
                                            getItemLabel={(facility) => `${facility.name}`}
                                            renderItem={(facility) => (
                                                <div className="space-y-2">
                                                    <div className="flex items-center justify-between">
                                                        <span className="font-medium">{facility.name}</span>
                                                    </div>
                                                    <div className="flex items-center gap-4 text-xs text-gray-600">
                                                        <span className="flex items-center gap-1">
                                                            <MapPin className="h-3 w-3" />
                                                            {facility.category_name}
                                                        </span>
                                                        <span className="flex items-center gap-1">
                                                            <Users className="h-3 w-3" />
                                                            Max: {facility.max_capacity}
                                                        </span>
                                                    </div>
                                                </div>
                                            )}
                                            emptyMessage="No facilities found matching your search"
                                            debounceMs={500}
                                        />
                                    </div>

                                    <div>
                                        <Label className="text-sm font-medium text-gray-700 mb-2 block">
                                            Member <span className="text-red-500">*</span>
                                        </Label>
                                        <SearchComboBox
                                            items={userOptions || []}
                                            onSearchChange={handleSearchMemberChange}
                                            onItemSelect={handleMemberSelect}
                                            value={searchMemberTerm}
                                            placeholder="Start typing to search members..."
                                            loading={loading}
                                            capitalize={true}
                                            getItemKey={(user) => user.id}
                                            getItemLabel={(user) => `${user.first_name} ${user.last_name}`}
                                            renderItem={(user) => (
                                                <div className="space-y-1">
                                                    <div className="flex gap-2 items-center">
                                                        <span className="capitalize font-medium">
                                                            {`${user.first_name} ${user.last_name}`}
                                                        </span>
                                                    </div>
                                                    <div className="text-muted-foreground text-xs">
                                                        {user.email}
                                                    </div>
                                                </div>
                                            )}
                                            emptyMessage="No members found matching your search"
                                            debounceMs={500}
                                        />
                                    </div>
                                </div>

                                {selectedFacility && (
                                    <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                                        <div className="flex items-center gap-2 text-blue-700 text-sm font-medium mb-2">
                                            <Info className="h-4 w-4" />
                                            Selected Facility Details
                                        </div>
                                        <div className="grid grid-cols-2 md:grid-cols-4 gap-1 text-sm">
                                            <div>
                                                <span className="text-gray-600">Category:</span>
                                                <p className="font-medium">{selectedFacility.category_name}</p>
                                            </div>
                                            <div>
                                                <span className="text-gray-600">Capacity:</span>
                                                <p className="font-medium">{selectedFacility.max_capacity} people</p>
                                            </div>
                                            {selectedFacility.price && selectedFacility.price > 0 && <div className="col-span-2">
                                                <span className="text-gray-600">Base Price:</span>
                                                <p className="font-medium text-green-600">
                                                    {formatToPKR(selectedSlot ? basePrice : effectivePrice)}
                                                    {selectedSlot && basePrice !== effectivePrice && (
                                                        <>
                                                            <span className="line-through text-gray-400 ml-2">
                                                                {formatToPKR(effectivePrice)}
                                                            </span>
                                                            <span className="text-xs text-orange-600 ml-1 font-semibold">
                                                                (Lowest Available)
                                                            </span>
                                                        </>
                                                    )}
                                                    {!selectedSlot && (calculateSpecialPrice || calculatePlanPrice) && currentScheduleData?.general_info?.price && (
                                                        <span className="line-through text-gray-400 ml-2">
                                                            {formatToPKR(currentScheduleData.general_info.price)}
                                                        </span>
                                                    )}
                                                    {!selectedSlot && calculateSpecialPrice && (
                                                        <span className="text-xs text-orange-600 ml-1 font-semibold">
                                                            (Special: {calculateSpecialPrice.name})
                                                        </span>
                                                    )}
                                                    {!selectedSlot && !calculateSpecialPrice && calculatePlanPrice && (
                                                        <span className="text-xs text-blue-600 ml-1 font-semibold">
                                                            (Plan: {calculatePlanPrice.plan_name})
                                                        </span>
                                                    )}
                                                </p>
                                            </div>}
                                        </div>
                                    </div>
                                )}

                                {/* Criteria Validation Status */}
                                {selectedMember && selectedFacility && (
                                    <div className="mt-4 space-y-3">
                                        {/* Loading State */}
                                        {isCriteriaValidating && (
                                            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                                                <div className="flex items-center gap-2 text-blue-700 text-sm font-medium">
                                                    <Clock className="h-4 w-4 animate-spin" />
                                                    Validating member criteria...
                                                </div>
                                            </div>
                                        )}

                                        {/* Success State */}
                                        {criteriaValidationData && !criteriaValidationError && (
                                            <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                                                <div className="flex items-center gap-2 text-green-700 text-sm font-medium">
                                                    <CheckCircle2 className="h-4 w-4" />
                                                    Member meets facility criteria
                                                </div>
                                            </div>
                                        )}

                                        {/* Error State */}
                                        {criteriaValidationError && (
                                            <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                                                <div className="flex items-center gap-2 text-red-700 text-sm font-medium">
                                                    <i className="fa fa-exclamation-triangle h-4 w-4"></i>
                                                    {criteriaValidationMessage}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </Card>

                            {/* Available Slots */}
                            {selectedFacility && (
                                <Card className="p-6 shadow-sm border-l-4 border-l-green-500">
                                    <div className="flex items-center gap-3 mb-4">
                                        <div className="p-2 bg-green-100 rounded-lg">
                                            <Calendar className="h-5 w-5 text-green-600" />
                                        </div>
                                        <div>
                                            <h2 className="text-lg font-semibold text-gray-900">Available Time Slots</h2>
                                            <p className="text-sm text-gray-600">Select an available time slot for this booking</p>
                                        </div>
                                    </div>
                                    <Separator className="mb-6" />

                                    <div className="min-w-[27rem] w-fit">
                                        <Label className="text-sm font-medium text-gray-700 mb-2 block">
                                            Time Slot <span className="text-red-500">*</span>
                                        </Label>
                                        <SlotSelector
                                            slots={facilitySlots || []}
                                            selectedSlot={selectedSlot}
                                            onSlotSelect={handleSlotSelect}
                                            isLoading={isLoadingFacilityById}
                                            action={action}
                                            selectedMember={selectedMember}
                                            scheduleData={currentScheduleData}
                                        />
                                    </div>

                                    {selectedSlot && (
                                        <div className="mt-4 space-y-4">
                                            <div className={`p-4 rounded-lg border ${calculateSpecialPrice ? 'bg-orange-50 border-orange-200' : 'bg-green-50 border-green-200'}`}>
                                                <div className={`flex items-center gap-2 text-sm font-medium mb-2 ${calculateSpecialPrice ? 'text-orange-700' : 'text-green-700'}`}>
                                                    <CheckCircle2 className="h-4 w-4" />
                                                    Selected Slot Details
                                                    {calculateSpecialPrice && (
                                                        <span className="ml-2 px-2 py-1 bg-orange-100 text-orange-800 text-xs rounded-full">
                                                            Special Pricing Applied
                                                        </span>
                                                    )}
                                                </div>
                                                <div className="grid grid-cols-3 md:grid-cols-7 gap-4 text-sm">
                                                    <div className="col-span-2">
                                                        <span className="text-gray-600">Date:</span>
                                                        <p className="font-medium">{format(new Date(selectedSlot.date), 'EEEE, MMM dd, yyyy')}</p>
                                                    </div>
                                                    <div>
                                                        <span className="text-gray-600">Time:</span>
                                                        <p className="font-medium">{selectedSlot.start_time} - {selectedSlot.end_time}</p>
                                                    </div>
                                                    <div>
                                                        <span className="text-gray-600">Duration:</span>
                                                        <p className="font-medium">{selectedSlot.duration_hours}h</p>
                                                    </div>
                                                    <div>
                                                        <span className="text-gray-600">Facility Type:</span>
                                                        <p className="font-medium">{selectedFacility.shareable_slots ? "Shared" : 'Exclusive'}</p>
                                                    </div>
                                                    <div className="col-span-2">
                                                        <span className="text-gray-600 ">Price:</span>
                                                        <p className="font-medium text-green-600 text-wrap">
                                                            {formatToPKR(effectivePrice)}
                                                            {calculateSpecialPrice && (
                                                                <span className="text-xs text-orange-600 ml-1">
                                                                    (Special: {calculateSpecialPrice.name})
                                                                </span>
                                                            )}
                                                            {!calculateSpecialPrice && calculatePlanPrice && (
                                                                <span className="text-xs text-blue-600 ml-1">
                                                                    (Plan: {calculatePlanPrice.plan_name})
                                                                </span>
                                                            )}
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Validation Status */}
                                            {(isValidating || isLoadingFacilityById) && (
                                                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                                                    <div className="flex items-center gap-2 text-blue-700 text-sm font-medium">
                                                        <Clock className="h-4 w-4 animate-spin" />
                                                        {isValidating ? "Validating booking availability..." : "Loading schedule details..."}
                                                    </div>
                                                </div>
                                            )}

                                            {validationData && !validationError && (
                                                <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                                                    <div className="flex items-center gap-2 text-green-700 text-sm font-medium">
                                                        <CheckCircle2 className="h-4 w-4" />
                                                        Slot is available for booking
                                                    </div>
                                                </div>
                                            )}
                                            {validationError && (
                                                <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                                                    <div className="flex items-center gap-2 text-red-700 text-sm font-medium">
                                                        <i className="fa fa-exclamation-triangle h-4 w-4"></i>
                                                        {validationMessage}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </Card>
                            )}

                            {/* Add-ons Subgrid */}
                            {selectedFacility && selectedSlot && scheduleAddOns && scheduleAddOns.length > 0 && (
                                <Card className="p-6 shadow-sm border-l-4 border-l-purple-500">
                                    <div className="flex items-center gap-3 mb-4">
                                        <div className="p-2 bg-purple-100 rounded-lg">
                                            <Plus className="h-5 w-5 text-purple-600" />
                                        </div>
                                        <div>
                                            <h2 className="text-lg font-semibold text-gray-900">Add-ons</h2>
                                            <p className="text-sm text-gray-600">Additional services for the selected facility</p>
                                        </div>
                                    </div>
                                    <Separator className="mb-6" />

                                    {/* Inclusive Add-ons */}
                                    {scheduleAddOns.filter(addOn => addOn.type?.toLowerCase().includes('inclusive')).length > 0 && (
                                        <div className="mb-6">
                                            <h3 className="text-md font-medium text-gray-900 mb-3 flex items-center gap-2">
                                                <i className="fa fa-lock text-blue-500"></i>
                                                Inclusive Add-ons
                                            </h3>
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                                {scheduleAddOns
                                                    .filter(addOn => addOn.type?.toLowerCase().includes('inclusive'))
                                                    .map(addOn => {
                                                        const isSelected = selectedAddOns.some(selected => selected.add_on_facility_id === addOn.add_on_facility_id);
                                                        return (
                                                            <div key={addOn.id} className="flex items-center justify-between p-4 bg-blue-50 rounded-lg border border-blue-200">
                                                                <div className="flex items-center gap-3">
                                                                    <Checkbox
                                                                        checked={true}
                                                                        disabled={true}
                                                                        className="opacity-50"
                                                                    />
                                                                    <div>
                                                                        <p className="font-medium text-gray-900">{addOn.name}</p>
                                                                        {addOn.description && (
                                                                            <p className="text-sm text-gray-600">{addOn.description}</p>
                                                                        )}
                                                                        <Badge variant="secondary" className="mt-1 bg-blue-100 text-blue-800">
                                                                            Inclusive
                                                                        </Badge>
                                                                    </div>
                                                                </div>
                                                                <div className="text-right">
                                                                    <p className="font-semibold text-blue-600">
                                                                        {formatToPKR(addOn.price || 0)}
                                                                    </p>
                                                                    <p className="text-xs text-gray-500">Required</p>
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                            </div>
                                        </div>
                                    )}

                                    {/* Exclusive Add-ons */}
                                    {scheduleAddOns.filter(addOn => !addOn.type?.toLowerCase().includes('inclusive')).length > 0 && (
                                        <div>
                                            <h3 className="text-md font-medium text-gray-900 mb-3 flex items-center gap-2">
                                                <i className="fa fa-plus-circle text-green-500"></i>
                                                Exclusive Add-ons
                                            </h3>
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                                {scheduleAddOns
                                                    .filter(addOn => !addOn.type?.toLowerCase().includes('inclusive'))
                                                    .map(addOn => {
                                                        const isSelected = selectedAddOns.some(selected => selected.add_on_facility_id === addOn.add_on_facility_id);
                                                        return (
                                                            <div key={addOn.id} className={`
                                                                flex items-center justify-between p-4 rounded-lg border transition-all
                                                                ${isSelected
                                                                    ? 'bg-green-50 border-green-200'
                                                                    : 'bg-gray-50 border-gray-200 hover:border-green-300'
                                                                }
                                                            `}>
                                                                <div className="flex items-center gap-3">
                                                                    <Checkbox
                                                                        checked={isSelected}
                                                                        onCheckedChange={(checked) => handleAddOnToggle(addOn, checked as boolean)}
                                                                    />
                                                                    <div>
                                                                        <p className="font-medium text-gray-900">{addOn.name}</p>
                                                                        {addOn.description && (
                                                                            <p className="text-sm text-gray-600">{addOn.description}</p>
                                                                        )}
                                                                        <Badge variant="outline" className="mt-1 border-green-300 text-green-700">
                                                                            Exclusive
                                                                        </Badge>
                                                                    </div>
                                                                </div>
                                                                <div className="text-right">
                                                                    <p className="font-semibold text-green-600">
                                                                        {formatToPKR(addOn.price || 0)}
                                                                    </p>
                                                                    <p className="text-xs text-gray-500">
                                                                        {isSelected ? 'Selected' : 'Optional'}
                                                                    </p>
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                            </div>
                                        </div>
                                    )}

                                    {scheduleAddOns.length === 0 && (
                                        <div className="text-center py-8">
                                            <Plus className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                                            <p className="text-gray-500">No add-ons available for this schedule</p>
                                        </div>
                                    )}
                                </Card>
                            )}

                            {/* Price Summary - Checkout Style */}
                            {selectedFacility && selectedSlot && (
                                <Card className="p-6 shadow-sm border-l-4 border-l-orange-500 bg-gradient-to-r from-orange-50 to-yellow-50">
                                    <div className="flex items-center gap-3 mb-4">
                                        <div className="p-2 bg-orange-100 rounded-lg">
                                            <span className="text-orange-600 font-semibold">Rs.</span>
                                        </div>
                                        <div className="flex-1">
                                            <h2 className="text-lg font-semibold text-gray-900">Price Summary</h2>
                                            <p className="text-sm text-gray-600">
                                                Checkout-style price breakdown
                                                {taxCalculation.taxPercentage > 0 && (
                                                    <span className={`ml-2 px-2 py-0.5 rounded text-xs font-medium ${orgTaxType?.tax_type === "inclusive"
                                                            ? "bg-blue-100 text-blue-700"
                                                            : "bg-purple-100 text-purple-700"
                                                        }`}>
                                                        {orgTaxType?.tax_type === "inclusive" ? "Tax Inclusive" : "Tax Exclusive"}
                                                    </span>
                                                )}
                                            </p>
                                        </div>
                                    </div>
                                    <Separator className="mb-4" />

                                    <div className="space-y-3">
                                        {/* Main Summary Table - Checkout Style */}
                                        <div>
                                            {/* Body */}
                                            <div className="px-4 space-y-3">
                                                {/* Facility Row */}
                                                <div className="flex justify-between items-start py-2">
                                                    <div className="flex-1">
                                                        <div className="font-medium text-gray-900">
                                                            {calculateSpecialPrice ? 'Special Price' :
                                                                calculatePlanPrice ? 'Price' :
                                                                    'Price'}
                                                        </div>
                                                        <div className="text-sm text-gray-600 mt-1">
                                                            {selectedSlot.duration_hours}h booking • {format(new Date(selectedSlot.date), 'MMM dd, yyyy')}
                                                        </div>
                                                        {(calculateSpecialPrice || calculatePlanPrice) && (
                                                            <Badge variant="outline" className={`mt-1 ${calculateSpecialPrice
                                                                    ? "bg-orange-50 text-orange-700 border-orange-200"
                                                                    : "bg-blue-50 text-blue-700 border-blue-200"
                                                                }`}>
                                                                {calculateSpecialPrice ? calculateSpecialPrice.name : calculatePlanPrice?.plan_name}
                                                            </Badge>
                                                        )}
                                                    </div>
                                                    <div className="text-right">
                                                        <div className="font-semibold text-gray-900">
                                                            {formatToPKR(effectivePrice)}
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Add-ons Section */}
                                                {selectedAddOns.length > 0 && (
                                                    <>
                                                        <div className="pt-2 border-t border-gray-100">
                                                            <div className="font-medium text-gray-900 mb-2">Add-ons</div>
                                                            {selectedAddOns.map(addOn => {
                                                                const originalAddOn = scheduleAddOns?.find(a => a.add_on_facility_id === addOn.add_on_facility_id);
                                                                const isInclusive = originalAddOn?.type?.toLowerCase().includes('inclusive');
                                                                return (
                                                                    <div key={addOn.id} className="flex justify-between items-center py-1">
                                                                        <div className="flex items-center gap-2">
                                                                            <span className="text-sm text-gray-700">
                                                                                {originalAddOn?.name || `Add-on ${addOn.id}`}
                                                                            </span>
                                                                            {isInclusive && (
                                                                                <Badge variant="outline" className="text-xs bg-blue-50 text-blue-600 border-blue-200">
                                                                                    Required
                                                                                </Badge>
                                                                            )}
                                                                        </div>
                                                                        <span className="text-sm font-medium text-gray-900">
                                                                            {formatToPKR(addOn.price)}
                                                                        </span>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>

                                                        {/* Add-ons Total Row */}
                                                        <div className="flex justify-between items-center pt-2 border-t border-gray-200">
                                                            <span className="font-medium text-gray-900">Add-ons Total</span>
                                                            <span className="font-semibold text-gray-900">
                                                                {formatToPKR(selectedAddOns.reduce((total, addOn) => total + addOn.price, 0))}
                                                            </span>
                                                        </div>
                                                    </>
                                                )}

                                                {/* Subtotal Row */}
                                                <div className="flex justify-between items-center pt-2 border-t border-gray-200">
                                                    <span className="font-medium text-gray-900">Subtotal</span>
                                                    <span className="font-semibold text-gray-900">
                                                        {formatToPKR(taxCalculation.subtotal)}
                                                    </span>
                                                </div>

                                                {/* Tax Row */}
                                                {taxCalculation.taxPercentage > 0 && (
                                                    <div className="flex justify-between items-center py-1">
                                                        <div className="flex items-center gap-2">
                                                            <span className={`text-sm ${orgTaxType?.tax_type === "inclusive" ? "text-blue-600" : "text-purple-600"}`}>
                                                                Tax ({taxCalculation.taxName} - {taxCalculation.taxPercentage}%)
                                                            </span>
                                                            {orgTaxType?.tax_type === "inclusive" && (
                                                                <Badge variant="outline" className="text-xs bg-blue-50 text-blue-600 border-blue-200">
                                                                    Included
                                                                </Badge>
                                                            )}
                                                        </div>
                                                        <span className={`text-sm font-medium ${orgTaxType?.tax_type === "inclusive" ? "text-blue-600" : "text-purple-600"}`}>
                                                            {orgTaxType?.tax_type === "inclusive" ? formatToPKR(Math.round(taxCalculation.taxAmount)) : `+${formatToPKR(Math.round(taxCalculation.taxAmount))}`}
                                                        </span>
                                                    </div>
                                                )}

                                                {/* Final Total Row */}
                                                <div className="flex justify-between items-center pt-3 mt-3 border-t-2 border-gray-300">
                                                    <span className="text-lg font-semibold text-gray-900">Total Amount</span>
                                                    <span className="text-xl font-bold text-green-700">
                                                        {formatToPKR(Math.round(totalPrice))}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Tax Information Card */}
                                        {/* {taxCalculation.taxPercentage > 0 && (
                                            <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
                                                <div className="flex items-start gap-2">
                                                    <Info className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                                                    <div className="text-sm text-blue-800">
                                                        <p className="font-medium mb-1">Tax Information</p>
                                                        <p>
                                                            {orgTaxType?.tax_type === "inclusive"
                                                                ? `All prices shown are tax-inclusive. The ${taxCalculation.taxName} (${taxCalculation.taxPercentage}%) is already included in the subtotal and total amounts.`
                                                                : `The ${taxCalculation.taxName} (${taxCalculation.taxPercentage}%) is calculated on the subtotal amount before add-ons.`
                                                            }
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                        )} */}
                                    </div>
                                </Card>
                            )}
                        </div>
                    </form>
                </FormProvider>
            </SheetContent>
        </Sheet>
    );
}

export default FacilityBookingForm;